import {Database, useRpcQuery, useViewQuery} from '@tnc/shared';
import {FC} from 'react';

import {profileDetailsDataItemT, profileDetailsTabListTabT} from '@/lib/types/form.types';

import {dateFormatter, usdFormatter} from '@/lib/helpers';

import {BrandDetailDistributors} from '@/pages/(shared)/brands/detail/components/brandDetailDistributors';
import {BrandDetailProducts} from '@/pages/(shared)/brands/detail/components/brandDetailProducts';
import {BrandDetailRetailers} from '@/pages/(shared)/brands/detail/components/brandDetailsRetailers';
import {useBrand} from '@/pages/(shared)/brands/detail/providers/brandDetailsProvider';

import {ProfileCardComponent, ProfileDetailsTabListComponent, profileCardSectionT} from '@/components';
import Page from '@/components/Page';

export const BrandDetails: FC = () => {
  const {brand} = useBrand();

  /**
   * Get order data
   */
  const {data: orderData} = useRpcQuery({
    functionName: 'brand_order_summary',
    functionArgs: {brand_id_input: brand?.brand_id ?? 0},
    // sortOption: {column: 'order_date', direction: 'desc', label: 'Order Date'},
    enabled: !!brand,
  });

  const {data: marketInfoData} = useViewQuery({
    view: 'brand_market_info',
    filters: [{column: 'brand_id', operator: 'eq', value: brand?.brand_id ?? null}],
    enabled: !!brand,
  });

  const headerInfo = {
    // Use the brand name or a fallback if it's not available
    header: brand?.brand_name || 'Unknown Brand',

    // Use the creation date as a subheader
    subHeader: brand?.date_created
      ? `Established on ${dateFormatter(brand.date_created, 'long')}`
      : 'Date not available',
  };

  const sections: Array<profileCardSectionT> = [
    {
      title: 'Brand Information',
      items: [
        {header: 'Name', content: brand?.brand_name},
        {header: 'Description', content: brand?.brand_description},
        {header: 'License Number', content: brand?.brand_license_number},
      ],
    },
    {
      title: 'Location Information',
      items: [
        {header: 'Address', content: `${brand?.brand_address}, ${brand?.brand_address2 || ''}`.trim()},
        {header: 'City', content: brand?.brand_city},
        {header: 'State', content: brand?.brand_state},
        {header: 'Postal Code', content: brand?.brand_postal_code},
      ],
    },
    {
      title: 'Contact Information',
      items: [
        {header: 'Phone', content: brand?.brand_phone},
        {header: 'Email', content: brand?.brand_email},
      ],
    },
    {
      title: 'Market Information',
      items: [
        {header: 'Distribution Inventory', content: marketInfoData?.data[0].similar_distribution_inventory},
        {header: 'Retailer Inventory', content: marketInfoData?.data[0].similar_retailer_inventory},
      ],
    },
  ];

  const tabs: profileDetailsTabListTabT[] = [
    {
      id: 'products',
      title: 'Products',
      icon: 'ri-shopping-cart-2-line',
      content: <BrandDetailProducts />,
    },

    {
      id: 'distributors',
      title: 'Distributors',
      icon: 'ri-store-2-line',
      content: <BrandDetailDistributors />,
    },
    {
      id: 'retailers',
      title: 'Retailers',
      icon: 'ri-store-2-line',
      content: <BrandDetailRetailers />,
    },
  ];

  const processedDataItems: profileDetailsDataItemT[] = [
    {
      type: 'generic',
      title: 'Pending Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['market_order_summary']['Returns'][0]) =>
              order.order_group === 'pending'
          )
          .map((order: Database['public']['Functions']['market_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'long', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
    {
      type: 'generic',
      title: 'In Progress Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['market_order_summary']['Returns'][0]) =>
              order.order_group === 'inprogress'
          )
          .map((order: Database['public']['Functions']['market_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'long', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
    {
      type: 'generic',
      title: 'Accepted/Rejected Orders',
      data:
        orderData?.data
          .filter((order: Database['public']['Functions']['market_order_summary']['Returns'][0]) =>
            ['accepted', 'rejected'].includes(order.order_group)
          )
          .map((order: Database['public']['Functions']['market_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'long', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
  ];

  return (
    <Page currentPage="Back to Brands">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          avatar={brand?.brand_logo_url || '/images/logo/logo.png'}
          name={brand?.brand_name || ''}
          headerInfo={headerInfo}
          className="col-span-12 xl:col-span-12 xxl:col-span-4"
          sections={sections}
          status={brand?.status}
        />
        <ProfileDetailsTabListComponent
          tabs={tabs}
          dataItems={processedDataItems}
          className="col-span-12 xl:col-span-12 xxl:col-span-8"
        />
      </div>
    </Page>
  );
};

export default BrandDetails;
